import React, { useEffect, useState } from "react";
import { Breadcrumb, Button, Card, Header, Icon, Loader, Message, Segment } from "semantic-ui-react";
import KpiTile from "../components/KpiTile";
import {
  useGetPermitsCountByStatesMetric,
  PermitMetricScopeInput,
  MetricDimensionKey,
} from "../operations/queries/getPermitsCountByStatesMetric";

interface Props {
  scope: PermitMetricScopeInput;
  onViewPermits: (narrowedScope: PermitMetricScopeInput, title: string) => void;
}

const DIMENSION_LABEL: Record<MetricDimensionKey, string> = {
  State: "State",
  Assignee: "Assignee",
  Month: "Month",
  PermitType: "Permit Type",
  Project: "Project",
  Register: "Register",
  None: "All",
};

/** One entry in the drill-down breadcrumb stack. */
interface DrillEntry {
  label: string;
  scope: PermitMetricScopeInput;
  dimension: MetricDimensionKey;
}

interface GroupTile {
  key: string;
  label: string;
  value: number;
  previousValue: number | null;
  deltaPercentage: number | null;
  trend: "FLAT" | "UP" | "DOWN";
}

/** Narrows the scope by pinning the selected group's dimension key. */
const narrowScope = (
  currentScope: PermitMetricScopeInput,
  dimension: MetricDimensionKey,
  group: GroupTile,
): PermitMetricScopeInput => {
  switch (dimension) {
    case "State":
      return { ...currentScope, stateKeys: [group.key] };
    case "PermitType":
      return { ...currentScope, permitTypeIds: [group.key] };
    case "Assignee":
      return { ...currentScope, teamMemberIds: [group.key] };
    case "Project":
      return { ...currentScope, projectIds: [group.key] };
    case "Register":
      return { ...currentScope, registerIds: [group.key] };
    default:
      return currentScope;
  }
};

/**
 * Returns the next grouping dimension after drilling into a group.
 * Returns null when already at the deepest drillable level (Register).
 */
const nextDrillDimension = (dimension: MetricDimensionKey): MetricDimensionKey | null => {
  switch (dimension) {
    case "State":
    case "Assignee":
    case "Month":
    case "PermitType":
      return "Project";
    case "Project":
      return "Register";
    default:
      return null;
  }
};

const GroupedPermitsBreakdownContainer = ({ scope, onViewPermits }: Props) => {
  const dimension: MetricDimensionKey = "State";
  const [drillStack, setDrillStack] = useState<DrillEntry[]>([]);
  const { getPermitsCountByStatesMetric, data, status } = useGetPermitsCountByStatesMetric();

  const currentScope = drillStack.length > 0 ? drillStack[drillStack.length - 1].scope : scope;
  const currentDimension = drillStack.length > 0 ? drillStack[drillStack.length - 1].dimension : dimension;

  useEffect(() => {
    if (scope.organizationId) {
      setDrillStack([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    if (currentScope.organizationId) {
      getPermitsCountByStatesMetric({
        variables: { query: { scope: currentScope, dimension: currentDimension } },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScope, currentDimension]);

  const metric = data?.permitsCountByStatesMetric;

  const groups: GroupTile[] = (metric?.groups ?? []).map((g) => ({
    key: g.dimensionKey,
    label: g.dimensionValue,
    value: g.value.value,
    previousValue: g.value.previousValue,
    deltaPercentage: g.value.deltaPercentage,
    trend: g.value.trend.key,
  }));

  const total = groups.reduce((sum, g) => sum + g.value, 0);
  const canDrillDown = nextDrillDimension(currentDimension) !== null;
  const nextLevel = nextDrillDimension(currentDimension);

  const handleTileClick = (group: GroupTile) => {
    if (canDrillDown) {
      const narrowed = narrowScope(currentScope, currentDimension, group);
      const next = nextDrillDimension(currentDimension)!;
      setDrillStack((prev) => [...prev, { label: group.label, scope: narrowed, dimension: next }]);
    } else {
      const narrowed = narrowScope(currentScope, currentDimension, group);
      const title = [...drillStack.map((e) => e.label), group.label].join(" › ");
      onViewPermits(narrowed, title);
    }
  };

  const handleDrillTo = (index: number) => setDrillStack((prev) => prev.slice(0, index));

  const handleBack = () => handleDrillTo(drillStack.length - 1);

  const backLabel =
    drillStack.length === 0
      ? ""
      : drillStack.length === 1
        ? `All by ${DIMENSION_LABEL[dimension]}`
        : drillStack[drillStack.length - 2].label;

  return (
    <>
      <Header as="h4" color="grey">
        Permits Breakdown
        <Header.Subheader>
          {status.loading && metric && <Loader active inline size="mini" />}
          {metric && (
            <>
              <strong>{total.toLocaleString()}</strong>
              {" permits total"}
            </>
          )}
        </Header.Subheader>
      </Header>
      {/* ── Drill-path bar: back button + breadcrumb ─────────────────── */}
      {drillStack.length > 0 && (
        <Segment color="olive" inverted>
          <Button size="mini" icon labelPosition="left" onClick={handleBack} title={`Go back to ${backLabel}`}>
            <Icon name="arrow left" />
            {backLabel}
          </Button>

          <Breadcrumb size="small" style={{ marginLeft: ".5em" }}>
            <Breadcrumb.Section link onClick={() => handleDrillTo(0)}>
              All
            </Breadcrumb.Section>
            {drillStack.map((entry, idx) => (
              <React.Fragment key={idx}>
                <Breadcrumb.Divider icon="right chevron" />
                {idx < drillStack.length - 1 ? (
                  <Breadcrumb.Section onClick={() => handleDrillTo(idx + 1)}>{entry.label}</Breadcrumb.Section>
                ) : (
                  <Breadcrumb.Section active>
                    {entry.label}
                    <span style={{ marginLeft: ".5em" }}>(by {DIMENSION_LABEL[entry.dimension]})</span>
                  </Breadcrumb.Section>
                )}
              </React.Fragment>
            ))}
          </Breadcrumb>
        </Segment>
      )}

      {/* ── Loading / error ─────────────────────────────────────────── */}
      {status.loading && !metric && <Loader active inline="centered" content="Loading breakdown…" />}
      {!status.loading && status.errors && status.errors.length > 0 && (
        <Message negative content={status.errors.join(" ")} />
      )}

      {/* ── Action hint ─────────────────────────────────────────────── */}
      {metric && groups.length > 0 && (
        <div style={{ fontSize: "0.82em", color: "#999", marginBottom: "0.6em" }}>
          <Icon name={canDrillDown ? "zoom in" : "list alternate outline"} />
          {canDrillDown
            ? `Click a tile to break down by ${DIMENSION_LABEL[nextLevel!]}`
            : "Click a tile to view its permits"}
        </div>
      )}

      {/* ── Tile grid ─────────────────────────────────────────────────── */}
      {metric && (
        <Card.Group stackable itemsPerRow={5}>
          {groups.map((group) => (
            <KpiTile
              key={group.key}
              label={group.label}
              value={group.value}
              previousValue={group.previousValue}
              deltaPercentage={group.deltaPercentage}
              trend={group.trend}
              onClick={() => handleTileClick(group)}
            />
          ))}
        </Card.Group>
      )}

      {metric && groups.length === 0 && !status.loading && (
        <Message info content="No data for the current selection." />
      )}
    </>
  );
};

export default GroupedPermitsBreakdownContainer;
